/**
 * A lista de clientes que a Matriz e o Kanban de Vendas usam: o porte de `carregarMapaClientesLead`
 * (`portal/admin.html:3541-3614`), ligando as três leituras à junção pura.
 *
 * POR QUE AS PEÇAS VÊM DA ÁREA DE CLIENTES: a lista é UMA só no legado (`todosClientes`), e a
 * frente de Clientes já portou as três leituras (`@studiolu/dados/areas/clientes`) e a junção
 * (`telas/clientes/regras.ts`). Uma segunda cópia aqui envelheceria de um lado só. Este arquivo só
 * as liga, na ordem e com o tratamento de erro do legado.
 */

// A fonte de dados, que a tela recebe de `usePainel().dados`.
import type { FonteDeDados } from '@studiolu/dados';
// As três leituras da lista, com as colunas, o filtro e a ordem do legado.
import { lerLeadsDaLista, lerPerfis, lerQuestionariosDaLista } from '@studiolu/dados/areas/clientes';
// A junção pura, que decide nome, e-mail, telefone, origem e produtos de cada cliente.
import { montarClientes, type ListaDeClientes } from '../clientes/regras.js';

/**
 * Lê e junta a lista. Falha de leitura NÃO interrompe, como no legado: ele registra no console e
 * segue com a lista vazia daquela parte (`admin.html:3546`, `3560`, `3578`). Por isso a função não
 * devolve `Resultado`: a tela do legado nunca mostra erro desta carga.
 */
export async function carregarMapaDeClientes(fonte: FonteDeDados): Promise<ListaDeClientes> {
  // 1 · Os cadastros, o mais recente primeiro.
  const perfis = await lerPerfis(fonte);
  // Mesma mensagem de console do legado, para quem investiga achar pelo mesmo texto.
  if (!perfis.ok) console.error('Erro ao carregar clientes:', perfis.erro.mensagem);
  const lista = perfis.ok ? perfis.dados : [];
  const ids = lista.map((p) => p.user_id);
  // 2 · Os questionários. Com a lista vazia a leitura nem vai à rede (atalho do legado, 3555).
  const questionarios = await lerQuestionariosDaLista(fonte, ids);
  if (!questionarios.ok) console.error('Erro ao carregar dados do questionário:', questionarios.erro.mensagem);
  // 3 · Os leads vinculados, DEPOIS dos questionários: é a ordem do legado.
  const leads = await lerLeadsDaLista(fonte, ids);
  if (!leads.ok) console.error('Erro ao carregar produtos dos clientes:', leads.erro.mensagem);
  // A junção, com a parte que falhou vazia.
  return montarClientes(lista, questionarios.ok ? questionarios.dados : [], leads.ok ? leads.dados : []);
}
